import { Button, Classes, Colors, Icon, Label } from "@blueprintjs/core";
import React, { useEffect, useMemo, useState } from "react";
import styled from "styled-components";
import {
  Box,
  CustomButton,
  CustomPopover,
  TitleComponent,
  MultiSelectCustomTarget,
  CustomSelect,
  ItemType,
  CustomMultiSelect,
  CustomDatePicker,
  CalendarModeT,
  Text,
} from "ui-components";
import useLocalStorage from "ui-io/use-local-storage";
import InputField from "../../components/shared/input-field";

import {
  filterArray,
  FilterT,
  FilterTArrayCodec,
  numberConditions,
  calendarConditions,
  conditions,
} from "./constants";
import { useMutation, useQuery } from "react-query";
import { saveSearchFilter } from "ui-io/queries/save-search-filter";
import { getSearchFiltersByUser } from "ui-io/queries/get-search-filter-by-user";

const LOCAL_STORAGE_KEY = "saved_filters";

type Props = {
  isOpen: boolean;
  setIsOpen: (val: boolean) => void;
  handleSearchRequest: (value: FilterT[]) => void;
};

const BasicFilter = ({ isOpen, setIsOpen, handleSearchRequest }: Props) => {
  return (
    <CustomPopover
      isOpen={isOpen}
      content={
        <BasicFilterContainer
          onClose={() => {
            setIsOpen(false);
          }}
          handleSearchRequest={handleSearchRequest}
        />
      }
      placement="bottom-start"
      interactionType="click-target"
      handleInteraction={(val) => {
        setIsOpen(val);
      }}
    >
      <CustomButton
        onClick={() => {
          setIsOpen(true);
        }}
        icon="filter"
        className="cursor-pointer"
        bradius="5px"
      >
        Filter
      </CustomButton>
    </CustomPopover>
  );
};
export default BasicFilter;

type PropsBasicFilterContainer = {
  onClose: () => void;
  handleSearchRequest: (value: FilterT[]) => void;
};
const BasicFilterContainer = ({
  onClose,
  handleSearchRequest,
}: PropsBasicFilterContainer) => {
  const [errors, setErrors] = useState<{ [key: string]: string }>({});
  const [openForFilterName, setOpenForFilterName] = useState(false);
  const [filterName, setFilterName] = useState("");
  const [currerntFilter, setCurrentFilter] = useState("Select Filter");

  const [filters, setFilters] = useLocalStorage<FilterT[]>(
    LOCAL_STORAGE_KEY,
    filterArray.filter((filter) => filter?.isSelected),
    FilterTArrayCodec
  );
  const [selectedFields, setSelectedFields] = useState<string[]>(
    filters.map((f: FilterT) => f.key)
  );

  const searchFiltersConnection = useQuery(
    getSearchFiltersByUser({ userID: "rahulcoudh" })
  );
  useEffect(() => {
    if (searchFiltersConnection.data?.length)
      setCurrentFilter(searchFiltersConnection.data[0].name);
  }, [searchFiltersConnection.data]);

  const saveSearchFilterMutation = useMutation(saveSearchFilter, {
    onSuccess: () => {},
    onError: () => {},
  });

  const { remainingFieldOptions, canAddMoreFilters, hasDummyKey } = useMemo(() => {
    const allFieldNames = filterArray.map((f) => f.key);

    const remainingField = allFieldNames.filter(
      (key) => !selectedFields.includes(key)
    );
    const moreFilters =
      filters.length < allFieldNames.length && remainingField.length > 0;
    const hasDummyKey = filters.some((f) => f.key === "dummyKey");

    return {
      remainingFieldOptions: remainingField,
      canAddMoreFilters: moreFilters,
      hasDummyKey,
    };
  }, [filterArray, selectedFields, filters]);

  const handleFilterChange = (id: string, key: keyof FilterT, value: any) => {
    setFilters((prev) => {
      const updated = prev.map((filter) => {
        if (filter.key === id) {
          const updatedFilter = { ...filter, [key]: value };
          if (key === "key") {
            setSelectedFields((prevSelected) => {
              const oldField = filter.key;
              const updatedSelected = prevSelected.filter(
                (f) => f !== oldField
              );
              return value ? [...updatedSelected, value] : updatedSelected;
            });
          }
          const validationError = validateFilter(updatedFilter);
          setErrors((prevErrors) => {
            const newErrors = { ...prevErrors };
            if (validationError) {
              newErrors[filter.key] = validationError;
            } else {
              delete newErrors[filter.key];
            }
            return newErrors;
          });

          return updatedFilter;
        }
        return filter;
      });
      return updated;
    });
  };
  const addFilter = () => {
    const newFilterExist = filters.find((f: FilterT) => f.key == "dummyKey");
    if (newFilterExist) {
      const validationError = validateFilter(newFilterExist);
      setErrors((prevErrors) => {
        const newErrors = { ...prevErrors };
        if (validationError) {
          newErrors["dummyKey"] = validationError;
        } else {
          delete newErrors["dummyKey"];
        }
        return newErrors;
      });
      return;
    }
    const newFilter = {
      key: "dummyKey",
      label: "Select",
      type: "",
      condition: "",
      values: [] as string[],
      logic: "AND" as "AND" | "OR",
    };
    const updated = [...filters, newFilter];
    setFilters(updated);
  };

  const removeFilter = (id: string) => {
    const toRemove = filters.find((f: FilterT) => f.key === id);
    if (toRemove?.key) {
      setSelectedFields((prev) => prev.filter((f) => f !== toRemove.key));
    }
    const updated = filters.filter((f: FilterT) => f.key !== id);
    setFilters(updated);
  };

  const handleSearch = async () => {
    let hasError = false;
    const newErrors: { [key: string]: string } = {};
    const reqBody: { [key: string]: { condition?: string; value: any } } = {};
    filters.forEach((filter: FilterT) => {
      const errorMsg = validateFilter(filter);
      if (errorMsg) {
        hasError = true;
        newErrors[filter.key] = errorMsg;
      } else {
        const field = filter.key;
        if (field)
          reqBody[field] = {
            condition: filter.condition,
            value: filter.values,
          };
      }
    });

    setErrors(newErrors);
    if (hasError) return;
    handleSearchRequest(filters);
    onClose();
  };

  const handleSelectField = (item: ItemType, filter: FilterT) => {
    const currentFilter = filterArray.find((f) => f.key === item.value);
    if (!currentFilter) return;

    setFilters((prev) => {
      const updated = prev.map((f) => {
        if (f.key === "dummyKey") {
          return {
            ...currentFilter,
            isSelected: true,
            logic: f.logic ?? "AND",
            values: [],
          };
        }
        if (
          f.key === filter.key &&
          filter.key !== "dummyKey" &&
          f.key !== item.value
        ) {
          return {
            ...f,
            key: currentFilter.key,
            label: currentFilter.label,
            type: currentFilter.type,
            items: currentFilter.items,
          };
        }
        return f;
      });

      setSelectedFields((prevSelected) => {
        const filtered = prevSelected.filter(
          (k) => k !== filter.key && k !== "dummyKey"
        );
        return filtered.includes(item.value)
          ? filtered
          : [...filtered, item.value];
      });

      setErrors((prevErrors) => {
        const { dummyKey, ...rest } = prevErrors;
        return rest;
      });

      return updated;
    });
  };

  const getCurrrentConditions = (type: string) => {
    if (type === "number") return numberConditions;
    if (type === "calendar") return calendarConditions;
    return conditions;
  };
  const handleDatePickerChange = (
    filterKey: string,
    mode: CalendarModeT,
    dates: (Date | null)[]
  ) => {
    if (mode === "in_date" || mode === "between_date") {
      handleFilterChange(
        filterKey,
        "values",
        dates.map((d) => (d ? d.toISOString() : null))
      );
    } else if (mode === "in_year" || mode === "between_year") {
      handleFilterChange(
        filterKey,
        "values",
        dates.map((d) => (d ? String(d.getFullYear()) : null))
      );
    } else if (mode === "in_quarter" || mode === "between_quarter") {
      handleFilterChange(
        filterKey,
        "values",
        dates.map((d) => {
          if (!d) return null;
          const year = d.getFullYear();
          const quarter = Math.floor(d.getMonth() / 3) + 1;
          return `${year}-Q${quarter}`;
        })
      );
    }
  };

  const handleSaveFilter = () => {
    if (filterName.trim() === "") {
      alert("Please enter a valid filter name.");
      return;
    }
    const filterData: { [key: string]: { condition?: string; value: any } } =
      {};
    filters.forEach((filter: FilterT) => {
      const field = filter.key;
      if (field)
        filterData[field] = {
          condition: filter.condition,
          value: filter.values,
        };
    });
    const filterObject = {
      metadata: { ...filterData },
      userName: "Rahul",
      userID: "rahulcoudh",
      name: filterName,
    };
    setOpenForFilterName(false);
    setFilterName("");
    saveSearchFilterMutation.mutate(filterObject);
  };

  const renderSelectComponent = (filter: FilterT) => {
    if (filter.type === "select") {
      return (
        <CustomSelect
          label={
            filter.items?.find((item) => item.value === filter.values[0])
              ?.label ?? `Select ${filter.label}`
          }
          isActiveItem={true}
          items={filter.items ?? []}
          values={filter.values}
          onSelect={(item: ItemType) => {
            handleFilterChange(filter.key, "values", [item.value]);
          }}
          filterable={filter.items && filter.items.length > 15}
        />
      );
    } else if (filter.type === "multiSelect") {
      const renderCustomTarget = (selectedItems: any[]) => (
        <MultiSelectCustomTarget
          count={selectedItems.length}
          text={`${filter.label} selected`}
        />
      );
      const customTarget = filter.values.length > 2;
      return (
        <CustomMultiSelect
          items={filter.items ?? []}
          values={filter.values ?? []}
          onSelect={(item: ItemType) => {
            let newValues = [...filter.values, item.value];
            if (filter.values.includes(item.value)) {
              newValues = filter.values.filter((v) => v !== item.value);
            }
            handleFilterChange(filter.key, "values", newValues);
          }}
          onRemove={(_value) => {
            const item = filter.items?.find((i) => i.label === _value);
            const valueStr = item ? item.value : String(_value);
            if (filter.values.includes(valueStr)) {
              const newValues = filter.values.filter((v) => v !== valueStr);
              handleFilterChange(filter.key, "values", newValues);
            }
          }}
          customTarget={customTarget ? renderCustomTarget : undefined}
        />
      );
    }
    return null;
  };

  return (
    <Container p={"large"}>
      <Box justify="between" items="center" flexDir="row">
        <Box
          justify="start"
          items="center"
          flexDir="row"
          mt={"small"}
          mb={"xlarge"}
          width="1/2"
          gap={"large"}
        >
          <TitleComponent marginTop="0px" className="mb-none">
            Filter
          </TitleComponent>
          {searchFiltersConnection.data?.length ? (
            <CustomSelect
              label={currerntFilter}
              isActiveItem={true}
              items={searchFiltersConnection.data.map((f: any) => ({
                label: f.name,
                value: f.name,
              }))}
              values={[currerntFilter]}
              onSelect={(item: ItemType) => {
                setCurrentFilter(item.value);
              }}
              labelFontSize={"12px"}
              errorClassName={"min-h-25"}
            />
          ) : null}
        </Box>
        <CloseIcon icon={"cross"} onClick={onClose} size={20} />
      </Box>

      {filters.map((filter: FilterT, index: number) => {
        const currentCondition = getCurrrentConditions(filter.type);
        return (
          <Box key={filter.key} flexDir="row" mb={"medium"} items="center">
            <Box gap={"xlarge"} mr={"medium"}>
              {index === 0 ? (
                <Text muted>Where</Text>
              ) : (
                <SelectCondition
                  value={filter.logic}
                  onChange={(e) =>
                    handleFilterChange(
                      filter.key,
                      "logic",
                      e.target.value as "AND" | "OR"
                    )
                  }
                  disabled={hasDummyKey && filter.key !== "dummyKey"}
                >
                  <option value="AND">AND</option>
                  <option value="OR">OR</option>
                </SelectCondition>
              )}
            </Box>
            <Box mr={"medium"} style={{ width: "250px" }}>
              <CustomSelect
                label={filter.label ?? "Select Field"}
                isActiveItem={false}
                items={filterArray
                  .filter((f) => remainingFieldOptions.includes(f.key))
                  .map((f) => ({ label: f.label, value: f.key }))}
                values={filter.values}
                onSelect={(item: ItemType) => handleSelectField(item, filter)}
                errorClassName={`${errors[filter.key] ? "intent-danger" : ""} ${hasDummyKey && filter.key !== "dummyKey" ? "cursor-not-allowed" : ""}`}
                disabled={hasDummyKey && filter.key !== "dummyKey"}
              />
            </Box>
            <Box mr={"medium"} style={{ width: "135px" }} >
              <CustomSelect
                items={currentCondition}
                values={filter.condition ? [filter.condition] : []}
                onSelect={(item: ItemType) => {
                  handleFilterChange(filter.key, "condition", item.value);
                }}
                label={
                  currentCondition.find((con) => con.value === filter.condition)
                    ?.label ?? currentCondition[0]?.label
                }
                isActiveItem={false}
                labelFontSize={
                  filter.condition == "between_quarter" ? "10px" : "12px"
                }
                disabled={hasDummyKey && filter.key !== "dummyKey"}
								errorClassName={`${hasDummyKey && filter.key !== "dummyKey" ? "cursor-not-allowed" : ""}`}
              />
            </Box>
            <Box mr={"medium"} style={{ width: "40%" }}>
              {filter.type === "select" || filter.type === "multiSelect" ? (
                renderSelectComponent(filter)
              ) : filter.type === "calendar" ? (
                <CustomDatePicker
                  mode={filter.condition as CalendarModeT}
                  value={
                    filter.condition === "in_year"
                      ? filter.values[0] || null
                      : filter.condition === "in_quarter"
                        ? filter.values[0] || null
                        : undefined
                  }
                  startDate={
                    filter.condition === "between_year"
                      ? filter.values[0] || null
                      : filter.condition === "between_quarter"
                        ? filter.values[0] || null
                        : filter.condition === "between_date"
                          ? filter.values[0] || null
                          : undefined
                  }
                  endDate={
                    filter.condition === "between_year"
                      ? filter.values[1] || null
                      : filter.condition === "between_quarter"
                        ? filter.values[1] || null
                        : filter.condition === "between_date"
                          ? filter.values[1] || null
                          : undefined
                  }
                  onValueChange={(dates) => {
                    handleDatePickerChange(
                      filter.key,
                      filter.condition as CalendarModeT,
                      dates
                    );
                  }}
                />
              ) : filter.type === "number" ? (
                filter.condition === "in_between" ? (
                  <Box flexDir="row" gap="small">
										<Box width={"1/2"}>
											<InputField
												mt="0px"
												bradius="4px"
												pd="5px 10px"
												name={`${filter.key}_from`}
												type="number"
												placeholder="From"
												inputContainerClass="mt-0"
												value={filter.values && filter.values[0] ? filter.values[0] : ""}
												onChange={(e: React.ChangeEvent<HTMLInputElement>) => {
													const toValue = filter.values && filter.values[1] ? filter.values[1] : "";
													handleFilterChange(filter.key, "values", [e.target.value, toValue]);
												}}
											/>
										</Box>
										<Box width={"1/2"}>
											<InputField
												mt="0px"
												bradius="4px"
												pd="5px 10px"
												name={`${filter.key}_to`}
												type="number"
												placeholder="To"
												inputContainerClass="mt-0"
												value={filter.values && filter.values[1] ? filter.values[1] : ""}
												onChange={(e: React.ChangeEvent<HTMLInputElement>) => {
													const fromValue = filter.values && filter.values[0] ? filter.values[0] : "";
													handleFilterChange(filter.key, "values", [fromValue, e.target.value]);
												}}
											/>
										</Box>
                  </Box>
                ) : (
                  <InputField
                    mt="0px"
                    bradius="4px"
                    pd="5px 10px"
                    name={filter.key}
                    type="number"
                    placeholder="Enter value"
                    inputContainerClass="mt-0"
                    value={
                      filter.values && filter.values[0] ? filter.values[0] : ""
                    }
                    onChange={(e: React.ChangeEvent<HTMLInputElement>) =>
                      handleFilterChange(filter.key, "values", [e.target.value])
                    }
                  />
                )
              ) : null}
            </Box>
            <Box>
              {filters.length > 1 && (
                <Button
                  onClick={() => removeFilter(filter.key)}
                  icon="trash"
                  minimal
                  size="small"
                />
              )}
            </Box>
          </Box>
        );
      })}

      <Box mt={"large"}>
        {canAddMoreFilters && (
          <CustomButton onClick={addFilter} icon="add" bradius="5px">
            Add Filter
          </CustomButton>
        )}
        {Object.keys(errors).map((key: string) => {
          return errors[key] ? (
            <Label className={"text-danger fs-10"}>{errors[key]}</Label>
          ) : null;
        })}
      </Box>
      <Box gap={"medium"} flexDir="row" justify="end" mt="xlarge">
        <CustomPopover
          isOpen={openForFilterName}
          popoverProps={{ minimal: true }}
          content={
            <Box borderRadius="small" pt={"medium"} pb="medium" px="large">
              <TitleComponent marginTop="0px" fontSize="16px">
                Filter Name
              </TitleComponent>
              <InputField
                mt="0px"
                bradius="4px"
                pd="5px 10px"
                name={"filterName"}
                type="text"
                placeholder="Enter filter name"
                inputContainerClass="mt-0"
                value={filterName}
                onChange={(e: React.ChangeEvent<HTMLInputElement>) => {
                  setFilterName(e.target.value);
                }}
              />
              <Box flexDir="row" justify="end" gap={"medium"} mt="large">
                <CustomButton
                  bradius="5px"
                  onClick={() => setOpenForFilterName(false)}
                >
                  Cancel
                </CustomButton>
                <CustomButton bradius="5px" onClick={handleSaveFilter}>
                  Save
                </CustomButton>
              </Box>
            </Box>
          }
          placement="top-end"
          interactionType="click"
          handleInteraction={(val) => {
            setOpenForFilterName(val);
          }}
        >
          <CustomButton bradius="5px"> Save Filter</CustomButton>
        </CustomPopover>

        <CustomButton onClick={handleSearch} bradius="5px">
          Search
        </CustomButton>
      </Box>
    </Container>
  );
};

const validateFilter = (filter: FilterT) => {
  if (!filter.key || filter.key == "dummyKey")
    return "Please pick a field from the dropdown you just added.";
  if (!filter.condition)
    return `Condition is required for field : ${filter.label}`;
  // if (
  //   filter.values === null ||
  //   filter.values === undefined ||
  //   (Array.isArray(filter.values) && filter.values?.length == 0)
  // ) {
  //   return `Value is required for field : ${filter.label}`;
  // }
  return null;
};

const Container = styled(Box)`
  width: 900px;
  max-height: 90vh;
  overflow-y: auto;
`;

const SelectCondition = styled.select`
  -webkit-appearance: none;
  -moz-appearance: none;
  appearance: none;
  padding-right: 1rem;
  border: none;
	background: ${({ theme: { darkMode } }) =>darkMode ? "#2F343C" : "white"};
	color: ${({ theme: { darkMode } }) =>darkMode ? Colors.GRAY5 : Colors.GRAY1};
`;

const CloseIcon = styled(Icon)`
  position: absolute;
  top: 20px;
  right: 20px;
  cursor: pointer;
`;